// Reports' arithmetic (spec 044 D71, D77–D80, D86): what src/services/reports.js reads, turned
// into the cards, the ring and the export. Pure, so Jest covers it.
import { toDate } from "./ticketPresentation";
import { money } from "./queuePresentation";
import { countText } from "./kitPresentation";
import { csvText } from "./transactionPresentation";
import { cairoDateKey, rangeLabel, signedUpLine } from "./reportPeriods";

const within = (value, { from, to }) => {
  const d = toDate(value);
  return !!d && d >= from && d < to;
};
const less = (n, k) => (n == null ? null : Math.max(0, n - k));
const round2 = (n) => Math.round(n * 100) / 100;

// D86: the first line of `reportReason` when a learner or a tutor says the other did not come —
// the labels the app's two report forms write (the app's src/utils/lessonReasons.js).
export const NO_SHOW_REASONS = ["Teacher didn't join the lesson", "Student didn't join the lesson"];

// D79: where a lesson sits in the ring. Pending splits on `now`: not started yet is Upcoming;
// started (under way, or ended and not marked finished) joins Reported in the last slice.
export const lessonBucket = (lesson, now) => {
  if (lesson.status === "Finished") return "completed";
  if (lesson.status === "Cancelled") return "cancelled";
  const start = toDate(lesson.startTimeUTC);
  return lesson.status === "Pending" && start && start >= now ? "upcoming" : "other";
};

// One window's lesson counts ({ total, completed, cancelled, upcoming } from the server) less
// the test accounts' lessons in that window; `other` is what the first four leave. D86: the
// finished lessons reported as a no-show (`noShows`, test accounts' already out) move from
// Completed to No-show; null when they could not be read.
const lessonsLess = (counts, testLessons, noShows, window, now) => {
  const mine = testLessons.filter((b) => within(b.startTimeUTC, window));
  const k = { completed: 0, cancelled: 0, upcoming: 0, other: 0 };
  mine.forEach((b) => {
    k[lessonBucket(b, now)] += 1;
  });
  const total = less(counts.total, mine.length);
  const finished = less(counts.completed, k.completed);
  const cancelled = less(counts.cancelled, k.cancelled);
  const upcoming = less(counts.upcoming, k.upcoming);
  const noShow = noShows ? noShows.filter((b) => within(b.startTimeUTC, window)).length : null;
  const known = [total, finished, cancelled, upcoming].every((n) => n != null);
  return {
    total,
    completed: noShow == null ? null : less(finished, noShow),
    cancelled,
    upcoming,
    noShow,
    other: known ? Math.max(0, total - finished - cancelled - upcoming) : null,
  };
};

// D74, D77: approved tutors (the reader leaves test accounts out) with at least one lesson in
// the window that is not cancelled, a test student's lessons not counted.
const activeIn = (tutors, testLessons, side, window) => {
  if (!tutors || tutors.some((t) => t[side].total == null || t[side].cancelled == null)) return null;
  return tutors.filter((t) => {
    const test = testLessons.filter(
      (b) => b.teacherId === t.id && b.status !== "Cancelled" && within(b.startTimeUTC, window)
    ).length;
    return t[side].total - t[side].cancelled - test > 0;
  }).length;
};

const NO_LESSONS = { total: null, completed: null, cancelled: null, upcoming: null, noShow: null, other: null };
const NOTHING = {
  revenue: { cur: null, prev: null },
  newStudents: { cur: null, prev: null },
  sessions: { cur: NO_LESSONS, prev: NO_LESSONS },
  active: { cur: null, prev: null },
  approved: null,
};

// D71: every number with the test accounts taken out. `raw` is getReport's answer, where a
// read that failed is null; it stays null here and the page shows "—". Without the test
// accounts' own rows nothing can be corrected, so every number is null then.
export const reportNumbers = (raw, windows) => {
  const { whole, soFar, now } = windows;
  const test = raw.internal;
  if (!test) return { ...NOTHING, failed: true };
  const signups = (w) => test.users.filter((u) => u.role === "student" && within(u.createdAt, w)).length;
  const paid = (w) =>
    test.topups
      .filter((r) => r.status === "succeeded" && within(r.createdAt, w))
      .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const pounds = (n, w) => (n == null ? null : Math.max(0, round2(n - paid(w))));
  const testIds = new Set(test.users.map((u) => u.id));
  const noShows = raw.noShows
    ? raw.noShows.filter((b) => b.status === "Finished" && !testIds.has(b.studentId) && !testIds.has(b.teacherId))
    : null;
  const n = {
    revenue: { cur: pounds(raw.revenue.cur, soFar), prev: pounds(raw.revenue.prev, soFar.prev) },
    newStudents: { cur: less(raw.newStudents.cur, signups(soFar)), prev: less(raw.newStudents.prev, signups(soFar.prev)) },
    sessions: {
      cur: lessonsLess(raw.sessions.cur, test.bookings, noShows, whole, now),
      prev: lessonsLess({ ...raw.sessions.prev, upcoming: 0 }, test.bookings, noShows, whole.prev, now),
    },
    active: {
      cur: activeIn(raw.tutors, test.bookings, "cur", whole),
      prev: activeIn(raw.tutors, test.bookings, "prev", whole.prev),
    },
    approved: raw.tutors ? raw.tutors.length : null,
  };
  const all = [
    n.revenue.cur, n.revenue.prev, n.newStudents.cur, n.newStudents.prev,
    ...Object.values(n.sessions.cur), ...Object.values(n.sessions.prev),
    n.active.cur, n.active.prev, n.approved,
  ];
  return { ...n, failed: all.some((x) => x == null) };
};

// D78: the change in %, one decimal; null when a side is missing or the period before had
// none (a percentage of nothing).
export const changePct = (cur, prev) =>
  cur == null || prev == null || prev === 0 ? null : Math.round(((cur - prev) / prev) * 1000) / 10;

// A card's chip: green up, red down, grey level; `before` names the stretch it compares with.
export const chipOf = (cur, prev, beforeWindow) => {
  const pct = changePct(cur, prev);
  if (pct == null) return null;
  const tone = pct > 0 ? "up" : pct < 0 ? "down" : "flat";
  const sign = pct > 0 ? "+" : pct < 0 ? "−" : "";
  return { tone, text: `${sign}${Math.abs(pct).toFixed(1)}%`, before: rangeLabel(beforeWindow) };
};

const num = (n) => (n == null ? "—" : countText(n));

// D77: the four cards, in Figma's order.
export const reportCards = (n, windows) => {
  const { key, whole, soFar } = windows;
  const s = n.sessions;
  return [
    {
      key: "revenue",
      label: "Gross revenue",
      value: n.revenue.cur == null ? "—" : `£${money(n.revenue.cur)}`,
      chip: chipOf(n.revenue.cur, n.revenue.prev, soFar.prev),
      caption: "Collected from families",
    },
    {
      key: "students",
      label: "New students",
      value: num(n.newStudents.cur),
      chip: chipOf(n.newStudents.cur, n.newStudents.prev, soFar.prev),
      caption: signedUpLine(key),
    },
    {
      key: "sessions",
      label: "Sessions booked",
      value: num(s.cur.total),
      chip: chipOf(s.cur.total, s.prev.total, whole.prev),
      caption: `${num(s.cur.completed)} completed so far`,
    },
    {
      key: "teachers",
      label: "Active teachers",
      value: num(n.active.cur),
      chip: chipOf(n.active.cur, n.active.prev, whole.prev),
      caption: `of ${num(n.approved)} approved tutor${n.approved === 1 ? "" : "s"}`,
    },
  ];
};

// D79, D86: the ring's five rows, in Figma's order, with the chart tone of each (figmaTokens'
// `chart`). The ring draws the first four; the fifth is what they leave uncovered, the ring's
// grey track — Figma's grey for that slice.
export const RING_ROWS = [
  { key: "completed", label: "Completed", tone: "green" },
  { key: "upcoming", label: "Upcoming", tone: "blue" },
  { key: "cancelled", label: "Cancelled", tone: "crimson" },
  { key: "noShow", label: "No-show", tone: "ochre" },
  { key: "other", label: "Live, reported or awaiting outcome", tone: "track" },
];

// The ring's slices and the counts under it; null until every count is in.
export const ringParts = (cur) => {
  if (Object.values(cur).some((x) => x == null)) return null;
  const rows = RING_ROWS.map((r) => ({ ...r, count: cur[r.key] }));
  const segments = cur.total
    ? rows
        .slice(0, 4)
        .filter((r) => r.count > 0)
        .map((r) => ({ key: r.key, tone: r.tone, fraction: r.count / cur.total }))
    : [];
  return { total: cur.total, rows, segments };
};

// D80: the export — every number for the period and the period before, the days each row
// counts, the change. Numbers go out as numbers; an unknown one as an empty cell.
export const REPORT_CSV_HEADER = ["Number", "This period", "Days", "Period before", "Days before", "Change %"];
export const reportCsv = (n, windows) => {
  const { key, now, whole, soFar } = windows;
  const toDateDays = [rangeLabel(soFar), rangeLabel(soFar.prev)];
  const wholeDays = [rangeLabel(whole), rangeLabel(whole.prev)];
  const row = (label, cur, prev, [days, daysBefore]) => [label, cur ?? "", days, prev ?? "", daysBefore, changePct(cur, prev) ?? ""];
  const s = n.sessions;
  const today = rangeLabel({ from: now, to: now });
  const rows = [
    row("Gross revenue (£)", n.revenue.cur, n.revenue.prev, toDateDays),
    row("New students", n.newStudents.cur, n.newStudents.prev, toDateDays),
    row("Sessions booked", s.cur.total, s.prev.total, wholeDays),
    row("Completed", s.cur.completed, s.prev.completed, wholeDays),
    row("Upcoming", s.cur.upcoming, s.prev.upcoming, wholeDays),
    row("Cancelled", s.cur.cancelled, s.prev.cancelled, wholeDays),
    row("No-show", s.cur.noShow, s.prev.noShow, wholeDays),
    row("Live, reported or awaiting outcome", s.cur.other, s.prev.other, wholeDays),
    row("Active teachers", n.active.cur, n.active.prev, wholeDays),
    ["Approved tutors", n.approved ?? "", today, "", "", ""],
  ];
  return { text: csvText(REPORT_CSV_HEADER, rows), filename: `reports-${key}-${cairoDateKey(now)}.csv` };
};
